import React, { useCallback, useContext, useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";
import {
  HvGlobalActions,
  HvGrid,
  HvTypography,
} from "@hitachivantara/uikit-react-core";

import { geocodeAddress, IconData, useEventTypes } from "lib/api";
import GenericMap, { MapMarker } from "components/common/GenericMap";

import { IncidentContext } from "lib/context/IncidentContext";

import useStyles from "./styles";

const defaultCameraIcon: IconData = {
  color: "#3498DB",
  icon: "camera",
  icon_type: "fontawesome",
};

const IncidentLocation: React.FC = () => {
  const classes = useStyles();
  const { t } = useTranslation("map");

  const { incident } = useContext(IncidentContext);

  const { data: eventTypeData } = useEventTypes({
    revalidateOnFocus: false,
  });

  const location = useMemo(
    () =>
      incident?.location != null
        ? {
            lng: incident.location.coordinates[0],
            lat: incident.location.coordinates[1],
          }
        : undefined,
    [incident?.location]
  );

  const markers = useMemo(() => {
    const toReturn: MapMarker[] = [];
    if (incident?.hv_entitys != null) {
      toReturn.push(
        ...incident.hv_entitys.map<MapMarker>((entity) => {
          const icon =
            entity.appearance?.icon_type != null
              ? entity.appearance
              : eventTypeData?.data.find((et) => et.id === entity.type_id)
                  ?.appearance;

          return {
            key: `entity_${entity.id}`,
            location: {
              lng: entity.location.coordinates[0],
              lat: entity.location.coordinates[1],
            },
            icon: { shape: "circle", ...icon },
            title: entity.name,
          };
        })
      );
    }

    if (incident?.inc_nearest_cameras != null) {
      toReturn.push(
        ...incident.inc_nearest_cameras.map<MapMarker>((camera) => {
          return {
            key: `camera_${camera.id}`,
            location: {
              lng: camera.location.coordinates[0],
              lat: camera.location.coordinates[1],
            },
            icon: {
              shape: "circle",
              ...(camera.appearance ?? defaultCameraIcon),
            },
            title: camera.name,
          };
        })
      );
    }

    return toReturn;
  }, [
    eventTypeData?.data,
    incident?.hv_entitys,
    incident?.inc_nearest_cameras,
  ]);

  const map = useRef<L.Map>();

  const onSearch = useCallback(async (search: string) => {
    const geocode = await geocodeAddress(search);

    if (map.current != null && geocode?.data.coordinates != null) {
      map.current.panTo(geocode?.data.coordinates);
    }
  }, []);

  if (incident == null) {
    return <></>;
  }

  return (
    <HvGrid container alignItems="flex-start">
      <HvGrid item xs={12}>
        <HvGlobalActions
          title={t("sections.location.title")}
          variant="section"
        />
        <HvTypography variant="normalText">
          {t("sections.location.description")}
        </HvTypography>
      </HvGrid>
      <HvGrid container item xs={12}>
        <HvGrid xs={12} sm={8} md={6} item>
          <GenericMap
            marker={incident.appearance}
            location={location}
            markers={markers}
            mapRef={map}
            onSearch={onSearch}
            searchPlaceholder={t("sections.location.map.searchPlaceholder")}
            searchButtonLabel={t("sections.location.map.searchButtonLabel")}
            clearButtonLabel={t("sections.location.map.clearButtonLabel")}
          />
        </HvGrid>
        <HvGrid container item xs={12} sm={4} md={6} alignContent="flex-start">
          <HvGrid xs={12} item>
            <HvTypography variant="highlightText" className={classes.label}>
              {t("sections.location.address")}
            </HvTypography>
            <HvTypography variant="normalText">
              {incident.inc_address}
            </HvTypography>
          </HvGrid>
          <HvGrid xs={12} item>
            <HvTypography variant="highlightText" className={classes.label}>
              {t("sections.location.latitude")}
            </HvTypography>
            <HvTypography variant="normalText">{location?.lat}</HvTypography>
          </HvGrid>
          <HvGrid xs={12} item>
            <HvTypography variant="highlightText" className={classes.label}>
              {t("sections.location.longitude")}
            </HvTypography>
            <HvTypography variant="normalText">{location?.lng}</HvTypography>
          </HvGrid>
        </HvGrid>
      </HvGrid>
    </HvGrid>
  );
};

export default IncidentLocation;
